/**
 * Simplified subscription service - Stripe handles all pricing
 */

import apiClient from '../utils/apiClient';
import mixpanelService from './mixpanelService';

class SubscriptionService {
  /**
   * Get available products and prices from backend
   */
  async getProducts() {
    try {
      const response = await apiClient.get('/products');

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const data = await response.json();
      return data.products;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  /**
   * Create a checkout session for subscription
   */
  async createCheckoutSession(billingPeriod = 'monthly') {
    try {
      mixpanelService.track('Checkout Started', { checkout_source: 'pricing' });
      const response = await apiClient.post('/subscriptions/create-checkout-session', {
        billing_period: billingPeriod,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
      return data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      mixpanelService.trackFailure('checkout_start', error, {
        checkout_source: 'pricing'
      });
      throw error;
    }
  }

  /**
   * Get user's subscription info including feature access
   */
  async getSubscriptionInfo() {
    try {
      const response = await apiClient.get('/subscriptions/info');

      if (!response.ok) {
        if (response.status === 404) {
          // No subscription, return free tier info
          return {
            status: 'free',
            tier: 'free',
            is_trial: false,
            can_access_monitor: false,
            can_join_studio: false,
            can_view_students: false
          };
        }
        throw new Error('Failed to fetch subscription info');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching subscription info:', error);
      throw error;
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription() {
    try {
      const response = await apiClient.post('/subscriptions/cancel');

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel subscription');
      }

      const data = await response.json();
      mixpanelService.track('Subscription Cancellation Scheduled');
      return data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      mixpanelService.trackFailure('subscription_cancel', error);
      throw error;
    }
  }

  /**
   * Create billing portal session
   */
  async createBillingPortal() {
    try {
      mixpanelService.track('Billing Portal Opened');
      const response = await apiClient.post('/subscriptions/billing-portal');

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create billing portal');
      }

      const data = await response.json();
      
      // Redirect to Stripe Billing Portal
      window.location.href = data.portal_url;
      
      return data;
    } catch (error) {
      console.error('Error creating billing portal:', error);
      mixpanelService.trackFailure('billing_portal_open', error);
      throw error;
    }
  }

  /**
   * Check if user has active subscription
   */
  async hasActiveSubscription() {
    try {
      const info = await this.getSubscriptionInfo();
      if (!info) {
        return false;
      }
      
      return info.status === 'active' || info.status === 'trialing';
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Create checkout session for a specific billing period
   */
  async createCheckoutSessionForPeriod(billingPeriod = 'monthly') {
    try {
      mixpanelService.track('Checkout Started', {
        checkout_source: 'monitor_gate',
        billing_period: billingPeriod
      });
      return await this.createCheckoutSession(billingPeriod);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      mixpanelService.trackFailure('checkout_start', error, {
        checkout_source: 'monitor_gate',
        billing_period: billingPeriod
      });
      throw error;
    }
  }

  /**
   * Create checkout session for default plan (monthly premium) - for backward compatibility
   */
  async createDefaultCheckoutSession() {
    return this.createCheckoutSessionForPeriod('monthly');
  }

  /**
   * Format price for display
   */
  formatPrice(amountInCents, currency = 'usd') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountInCents / 100);
  }
}

// Export singleton instance
const subscriptionService = new SubscriptionService();
export default subscriptionService;
